'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import PropTypes from 'prop-types';
import CartItem from './CartItem';
import CartSummary from './CartSummary';
import EmptyCart from './EmptyCart';
import SavedItems from './SavedItems';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import { useCart } from '@/contexts/CartContext'; //  ONLY source of truth
import { useAuth } from '@/contexts/AuthContext'; //  LOGIN CHECK

const SAVED_KEY = 'athena_saved_items';

const ShoppingCartInteractive = ({ initialCartData, initialSavedData }) => {
  const router = useRouter();

  //  Auth user
  const { user } = useAuth();

  //  Context cart (Navbar bhi isi se update hota hai)
  const { cart, setCart } = useCart();

  const [savedItems, setSavedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  //  Load ONLY savedItems from localStorage
  //  Cart ko localStorage se yaha load/save mat karo (CartContext karega)
  useEffect(() => {
    try {
      const savedForLater = localStorage.getItem(SAVED_KEY);
      if (savedForLater) {
        setSavedItems(JSON.parse(savedForLater));
      } else {
        setSavedItems(initialSavedData || []);
      }
    } catch (error) {
      console.error('Error loading saved items:', error);
      setSavedItems(initialSavedData || []);
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Save savedItems to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SAVED_KEY, JSON.stringify(savedItems || []));
    } catch (e) {
      console.error('Error saving saved items:', e);
    }
  }, [savedItems]);

  //Quantity Update
  const handleUpdateQuantity = async (itemId, newQuantity) => {
    setCart((prev) =>
      (prev || []).map((item) =>
        String(item?.id) === String(itemId)
          ? { ...item, quantity: newQuantity }
          : item
      )
    );

    toast.success('Cart updated');
  };

  //Delete Item (Sonner Confirm)
  const handleRemoveItem = (itemId) => {
    const item = (cart || [])?.find((i) => String(i?.id) === String(itemId));

    toast('Remove item from cart?', {
      description: item?.name || 'This product will be removed',
      action: {
        label: 'Delete',
        onClick: () => {
          setCart((prev) =>
            (prev || []).filter((i) => String(i?.id) !== String(itemId))
          );
          toast.success('Item removed from cart');
        },
      },
      cancel: { label: 'Cancel' },
    });
  };

  //Save for Later
  const handleSaveForLater = (itemId) => {
    const itemToSave = (cart || [])?.find(
      (item) => String(item?.id) === String(itemId)
    );

    if (itemToSave) {
      setSavedItems((prev) => [...(prev || []), itemToSave]);
      setCart((prev) =>
        (prev || []).filter((item) => String(item?.id) !== String(itemId))
      );
      toast.success('Saved for later');
    }
  };

  //Move back to Cart
  const handleMoveToCart = (itemId) => {
    const itemToMove = (savedItems || [])?.find(
      (item) => String(item?.id) === String(itemId)
    );

    if (itemToMove) {
      setCart((prev) => [
        ...(prev || []),
        { ...itemToMove, quantity: itemToMove?.minOrderQty || 1 },
      ]);

      setSavedItems((prev) =>
        (prev || []).filter((item) => String(item?.id) !== String(itemId))
      );

      toast.success('Moved to cart');
    }
  };

  //Remove Saved Item
  const handleRemoveSaved = (itemId) => {
    const item = (savedItems || [])?.find(
      (i) => String(i?.id) === String(itemId)
    );

    toast('Remove saved item?', {
      description: item?.name || 'This item will be removed',
      action: {
        label: 'Remove',
        onClick: () => {
          setSavedItems((prev) =>
            (prev || []).filter((i) => String(i?.id) !== String(itemId))
          );
          toast.success('Saved item removed');
        },
      },
      cancel: { label: 'Cancel' },
    });
  };

  const handleContinueShopping = () => {
    router.push('/product-catalog');
  };

  //FINAL FIX: Checkout Guard
  const handleProceedToCheckout = () => {
    // 1) Cart empty
    if (!cart || cart?.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // 2) Not logged in
    if (!user?.id) {
      toast.error('You have to login first');
      router.push('/login'); // change route if needed
      return;
    }

    // 3) Proceed
    router.push('/checkout');
  };

  const subtotal = (cart || []).reduce(
    (sum, item) => sum + Number(item?.price || 0) * Number(item?.quantity || 0),
    0
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading cart...</p>
        </div>
      </div>
    );
  }

  if (!cart || cart?.length === 0) {
    return (
      <>
        <EmptyCart onContinueShopping={handleContinueShopping} />
        <SavedItems
          savedItems={savedItems}
          onMoveToCart={handleMoveToCart}
          onRemoveSaved={handleRemoveSaved}
        />
      </>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
      {/* Cart Items Section */}
      <div className="lg:col-span-2">
        <div className="bg-card border border-border rounded-lg p-4 md:p-6 lg:p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl lg:text-2xl font-semibold text-foreground">
              Shopping Cart ({cart?.length} {cart?.length === 1 ? 'item' : 'items'})
            </h2>

            {cart?.length > 0 && (
              <button
                onClick={() => {
                  toast('Clear entire cart?', {
                    description: 'All items will be removed',
                    action: {
                      label: 'Clear',
                      onClick: () => {
                        setCart([]);
                        toast.success('Cart cleared');
                      },
                    },
                    cancel: { label: 'Cancel' },
                  });
                }}
                className="text-sm caption text-error hover:text-error/80 transition-smooth focus:outline-none focus:ring-2 focus:ring-ring rounded px-2 py-1"
              >
                Clear Cart
              </button>
            )}
          </div>

          <div className="space-y-4">
            {cart?.map((item) => (
              <CartItem
                key={item?.id}
                item={item}
                onUpdateQuantity={handleUpdateQuantity}
                onRemove={handleRemoveItem}
              />
            ))}
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="bg-muted border border-border rounded-lg p-4 md:p-6 mb-6">
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
            Bulk Actions
          </h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => {
                (cart || [])?.forEach((item) => handleSaveForLater(item?.id));
              }}
              className="flex items-center gap-2 px-4 py-2 bg-background hover:bg-background/80 text-foreground text-sm font-medium rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <Icon name="BookmarkIcon" size={18} />
              <span>Save All for Later</span>
            </button>

            <button
              onClick={() => toast.info('Cart sharing coming soon!')}
              className="flex items-center gap-2 px-4 py-2 bg-background hover:bg-background/80 text-foreground text-sm font-medium rounded-md transition-smooth focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <Icon name="ShareIcon" size={18} />
              <span>Share Cart</span>
            </button>
          </div>
        </div>

        {/* Saved Items */}
        <SavedItems
          savedItems={savedItems}
          onMoveToCart={handleMoveToCart}
          onRemoveSaved={handleRemoveSaved}
        />
      </div>

      {/* Cart Summary Section */}
      <div className="lg:col-span-1">
        <CartSummary
          subtotal={subtotal}
          cartItems={cart}
          onProceedToCheckout={handleProceedToCheckout}
        />
      </div>
    </div>
  );
};

ShoppingCartInteractive.propTypes = {
  initialCartData: PropTypes.array,
  initialSavedData: PropTypes.array,
};

export default ShoppingCartInteractive;